import { JsonObject, JsonProperty, JsonType } from "ta-json";

import { CultureLoadOption, type ICultureLoadOption } from "./culture-load-option";
import { EntityLoadConfigurationBuilder, IEntityLoadConfigurationBuilder } from "./entity-load-configuration-builder";
import { type IPropertyLoadOption, PropertyLoadOption } from "./property-load-option";
import { type IRelationLoadOption, RelationLoadOption } from "./relation-load-option";

export interface IEntityLoadConfiguration {
    /**
     * Gets the culture load option.
     */
    cultureLoadOption: ICultureLoadOption;

    /**
     * Gets the property load option.
     */
    propertyLoadOption: IPropertyLoadOption;

    /**
     * Gets the relation load option.
     */
    relationLoadOption: IRelationLoadOption;

    /**
     * Creates a {@link IEntityLoadConfigurationBuilder} from the current {@link IEntityLoadConfiguration}.
     * The builder can extend or overwrite the configuration of the current {@link IEntityLoadConfiguration}.
     */
    builder(): IEntityLoadConfigurationBuilder;

    /**
     * Creates a deep clone of this instance.
     */
    deepClone(): IEntityLoadConfiguration;

    // // deepEquals(other: IEntityLoadConfiguration): boolean;
}

@JsonObject()
export class EntityLoadConfiguration implements IEntityLoadConfiguration {
    /**
     * Only load system properties.
     */
    static Minimal: IEntityLoadConfiguration = new EntityLoadConfiguration(
        CultureLoadOption.None,
        PropertyLoadOption.None,
        RelationLoadOption.None
    );

    /**
     * Gets the entities in default culture with all properties and no relations.
     */
    static Default: IEntityLoadConfiguration = new EntityLoadConfiguration(
        CultureLoadOption.Default,
        PropertyLoadOption.All,
        RelationLoadOption.None
    );

    /**
     * Get the entities in default culture with all properties and relations.
     */
    static DefaultCultureFull: IEntityLoadConfiguration = new EntityLoadConfiguration(
        CultureLoadOption.Default,
        PropertyLoadOption.All,
        RelationLoadOption.All
    );

    /**
     * Get the entity in all installed cultures with all properties and relations.
     */
    static Full: IEntityLoadConfiguration = new EntityLoadConfiguration(
        CultureLoadOption.All,
        PropertyLoadOption.All,
        RelationLoadOption.All
    );

    @JsonProperty("culture_option")
    @JsonType(CultureLoadOption)
    cultureLoadOption: ICultureLoadOption;

    @JsonProperty("property_option")
    @JsonType(PropertyLoadOption)
    propertyLoadOption: IPropertyLoadOption;

    @JsonProperty("relation_option")
    @JsonType(RelationLoadOption)
    relationLoadOption: IRelationLoadOption;

    constructor(
        cultureLoadOption?: ICultureLoadOption,
        propertyLoadOption?: IPropertyLoadOption,
        relationLoadOption?: IRelationLoadOption
    ) {
        if (cultureLoadOption) this.cultureLoadOption = cultureLoadOption;
        if (propertyLoadOption) this.propertyLoadOption = propertyLoadOption;
        if (relationLoadOption) this.relationLoadOption = relationLoadOption;
    }

    builder(): IEntityLoadConfigurationBuilder {
        return new EntityLoadConfigurationBuilder(this);
    }

    deepClone(): IEntityLoadConfiguration {
        return new EntityLoadConfiguration(
            this.cultureLoadOption && this.cultureLoadOption.deepClone(),
            this.propertyLoadOption && this.propertyLoadOption.deepClone(),
            this.relationLoadOption && this.relationLoadOption.deepClone()
        );
    }
}
